/**
 * Offline Outbox + PIN verifier (Phase 5).
 * Reuses the authoritative Phase 4 /api/outbox/sync endpoint on the server.
 */
import { db } from "./db";
import api from "@/lib/api";

/** Enqueue a mutation event. Returns the event_id for the UI. */
export async function enqueueEvent({ workspace_id, device_id, branch_id, event_type, payload }) {
  const event_id = `evt_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
  const client_transaction_id = `tx_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
  await db.outbox.add({
    event_id,
    client_transaction_id,
    workspace_id,
    device_id,
    branch_id,
    event_type,
    payload,
    created_at_device: new Date().toISOString(),
    status: "PENDING_SYNC",
    retry_count: 0,
    last_error: null,
  });
  // Kick adaptive scheduler so pending events are flushed soon (spec §C).
  try { kickSyncScheduler(workspace_id); } catch (_) {}
  return event_id;
}

export async function pendingCount(workspace_id) {
  return db.outbox.where({ workspace_id }).and((x) => x.status === "PENDING_SYNC" || x.status === "SYNC_ERROR").count();
}

let _syncRunning = false;
/** Push PENDING_SYNC events for this workspace to the server. */
export async function syncOutbox(workspace_id) {
  if (_syncRunning) return { skipped: true };
  if (!navigator.onLine) return { offline: true };
  _syncRunning = true;
  try {
    const pending = await db.outbox.where({ workspace_id })
      .and((x) => x.status === "PENDING_SYNC" || x.status === "SYNC_ERROR")
      .limit(50).toArray();
    if (!pending.length) return { synced: 0 };
    // Mark SYNCING
    await Promise.all(pending.map((p) => db.outbox.update(p.event_id, { status: "SYNCING" })));
    try {
      const { data } = await api.post("/outbox/sync", { events: pending });
      for (const r of (data.results || [])) {
        if (r.status === "synced") {
          await db.outbox.update(r.event_id, { status: "SYNCED", server_result: r });
        } else {
          const row = await db.outbox.get(r.event_id);
          await db.outbox.update(r.event_id, {
            status: "SYNC_ERROR",
            last_error: r.error || "unknown",
            retry_count: (row?.retry_count || 0) + 1,
          });
        }
      }
      return { synced: (data.results || []).filter((r) => r.status === "synced").length };
    } catch (e) {
      // Network/server failure — mark SYNC_ERROR for retry (never silently drop).
      for (const p of pending) {
        await db.outbox.update(p.event_id, {
          status: "SYNC_ERROR",
          last_error: e?.message || "network",
          retry_count: (p.retry_count || 0) + 1,
        });
      }
      throw e;
    }
  } finally { _syncRunning = false; }
}

// ---------------------------------------------------------------------------
// Adaptive sync scheduler (spec §C — no infinite 5s polling on mobile).
// Backoff schedule when the outbox has pending events and the device is online.
// Resets to the first step whenever a new event is queued, the connection
// returns, or the user hits "Sync Now".
// ---------------------------------------------------------------------------
const BACKOFF_STEPS_MS = [5_000, 10_000, 30_000, 60_000, 120_000];
let _schedulerTimer = null;
let _schedulerStep = 0;
let _schedulerWs = null;

function _clearScheduler() {
  if (_schedulerTimer) { clearTimeout(_schedulerTimer); _schedulerTimer = null; }
}

async function _tick() {
  _schedulerTimer = null;
  if (!_schedulerWs) return;
  if (!navigator.onLine) { _scheduleNext(); return; }
  const count = await db.outbox.where({ workspace_id: _schedulerWs })
    .and((x) => x.status === "PENDING_SYNC" || x.status === "SYNC_ERROR").count();
  if (count === 0) {
    // Nothing pending — no further polling. Scheduler is event-driven again.
    _schedulerStep = 0;
    return;
  }
  try { await syncOutbox(_schedulerWs); } catch (_) { /* already recorded */ }
  // Backoff: step up unless something was flushed (then reset).
  const after = await db.outbox.where({ workspace_id: _schedulerWs })
    .and((x) => x.status === "PENDING_SYNC" || x.status === "SYNC_ERROR").count();
  _schedulerStep = after < count ? 0 : Math.min(_schedulerStep + 1, BACKOFF_STEPS_MS.length - 1);
  _scheduleNext();
}

function _scheduleNext() {
  _clearScheduler();
  const delay = BACKOFF_STEPS_MS[_schedulerStep] || 60_000;
  _schedulerTimer = setTimeout(_tick, delay);
}

/** Public: kick the adaptive scheduler. Call on login, on outbox write, on
 * online event. Safe to call frequently — it resets backoff and re-arms. */
export function kickSyncScheduler(workspace_id) {
  _schedulerWs = workspace_id;
  _schedulerStep = 0;
  _scheduleNext();
}

/** Public: stop the scheduler on logout / workspace switch. */
export function stopSyncScheduler() {
  _clearScheduler();
  _schedulerWs = null;
  _schedulerStep = 0;
}

// Wire the online event to re-kick immediately (spec §K).
if (typeof window !== "undefined") {
  window.addEventListener("online", () => {
    if (_schedulerWs) kickSyncScheduler(_schedulerWs);
  });
}

// ---------------------------------------------------------------------------
// Offline PIN verifier (PBKDF2-SHA256, same params as the server).
// ---------------------------------------------------------------------------
async function pbkdf2(pin, saltBytes, iterations) {
  const key = await crypto.subtle.importKey(
    "raw", new TextEncoder().encode(pin), { name: "PBKDF2" }, false, ["deriveBits"],
  );
  const bits = await crypto.subtle.deriveBits(
    { name: "PBKDF2", salt: saltBytes, iterations, hash: "SHA-256" }, key, 32 * 8,
  );
  return new Uint8Array(bits);
}

function b64ToBytes(b64) {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

function bytesEqual(a, b) {
  if (a.length !== b.length) return false;
  let diff = 0;
  for (let i = 0; i < a.length; i++) diff |= a[i] ^ b[i];
  return diff === 0;
}

const LOCK_MIN = 15;
const MAX_FAIL = 5;

export async function verifyOfflinePin({ workspace_id, username, pin }) {
  const row = await db.pin_verifiers.where({ workspace_id })
    .and((v) => v.username === (username || "").toLowerCase()).first();
  if (!row) return { ok: false, reason: "unknown_user" };
  // Lockout check
  if (row.locked_until && new Date(row.locked_until) > new Date()) {
    return { ok: false, reason: "locked_out", locked_until: row.locked_until };
  }
  const salt = b64ToBytes(row.offline_salt_b64);
  const expect = b64ToBytes(row.offline_verifier_b64);
  const got = await pbkdf2(pin, salt, row.offline_iter || 120000);
  if (bytesEqual(got, expect)) {
    await db.pin_verifiers.update([workspace_id, row.staff_id],
      { failed_attempts: 0, locked_until: null, last_login_at: new Date().toISOString() });
    return { ok: true, staff: { staff_id: row.staff_id, name: row.name, role: row.role } };
  }
  const attempts = (row.failed_attempts || 0) + 1;
  const patch = { failed_attempts: attempts };
  if (attempts >= MAX_FAIL) {
    patch.locked_until = new Date(Date.now() + LOCK_MIN * 60000).toISOString();
    patch.failed_attempts = 0;
  }
  await db.pin_verifiers.update([workspace_id, row.staff_id], patch);
  return { ok: false, reason: patch.locked_until ? "locked_out" : "bad_pin" };
}

export async function cacheOfflineBundle(bundle) {
  const ws = bundle.workspace_id;
  await db.transaction("rw",
    [db.products, db.categories, db.pin_verifiers, db.inventory_states, db.entitlements],
    async () => {
      // Replace products/categories within this workspace (safe partition).
      await db.products.where({ workspace_id: ws }).delete();
      await db.categories.where({ workspace_id: ws }).delete();
      await db.pin_verifiers.where({ workspace_id: ws }).delete();
      await db.inventory_states.where({ workspace_id: ws }).delete();
      await db.products.bulkPut((bundle.products || []).map((p) => ({ ...p, workspace_id: ws })));
      await db.categories.bulkPut((bundle.categories || []).map((c) => ({ ...c, workspace_id: ws })));
      await db.pin_verifiers.bulkPut((bundle.staff || []).map((s) => ({
        workspace_id: ws, staff_id: s.staff_id, username: s.username, name: s.name, role: s.role,
        offline_salt_b64: s.offline_salt_b64, offline_verifier_b64: s.offline_verifier_b64,
        offline_iter: s.offline_iter, failed_attempts: 0, locked_until: null,
      })));
      await db.inventory_states.bulkPut((bundle.inventory_states || []).map((st) => ({
        workspace_id: ws, inventory_item_id: st.inventory_item_id,
        on_hand: st.on_hand, wac: st.wac, updated_at: st.updated_at,
      })));
      if (bundle.entitlement) {
        await db.entitlements.put({ workspace_id: ws, ...bundle.entitlement });
      }
    },
  );
}

export async function offlineEntitlement(workspace_id) {
  return db.entitlements.get(workspace_id);
}
